import { Body, Controller, Delete, Get, HttpStatus, Param, Patch, Post, Query, Req } from '@nestjs/common';
import { ApiOperation, ApiParam, ApiQuery, ApiResponse, ApiTags, ApiBody, ApiConsumes, ApiExtraModels, ApiProduces } from '@nestjs/swagger';
import { FastifyRequest } from 'fastify';
import { ApiResponseDto } from '../../../common/dtos/api-response.dto';
import { User } from '../../user/entities/user.entity';
import { ChapterService } from '../services/chapter.service';
import { CreateChapterDto } from '../dto/create-chapter.dto';
import { UpdateChapterDto } from '../dto/update-chapter.dto';
import { ChapterResponseDto } from '../dto/chapter-response.dto';

@ApiTags('Chapters')
@ApiExtraModels(CreateChapterDto, UpdateChapterDto)
@Controller('chapters')
export class ChapterController {
  constructor(private readonly chapterService: ChapterService) {}

  @Post()
  @ApiConsumes('application/json')
  @ApiProduces('application/json')
  @ApiOperation({ summary: 'Create a new chapter' })
  @ApiResponse({ status: 201, description: 'The chapter has been successfully created.', type: ChapterResponseDto })
  @ApiResponse({ status: 400, description: 'Bad Request' })
  @ApiBody({
    description: 'JSON payload to create a chapter',
    schema: {
      type: 'object',
      properties: {
        title: { type: 'string', example: 'Q1 Announcements' },
        newsfeeds: {
          type: 'array',
          items: { type: 'number' },
          example: [1, 2, 3],
        },
      },
      required: ['title'],
    },
  })
  async create(
    @Req() request: FastifyRequest & { user: User },
    @Body() body: CreateChapterDto,
  ): Promise<ApiResponseDto<ChapterResponseDto>> {
    const chapter = await this.chapterService.create(body, request.user);
    return {
      success: true,
      message: 'Chapter created successfully',
      data: chapter,
      statusCode: HttpStatus.CREATED,
    };
  }

  @Get()
  @ApiOperation({ summary: 'Get all chapters with pagination' })
  @ApiQuery({ name: 'page', description: 'The page number | default is 1', required: false, example: '1' })
  @ApiQuery({ name: 'limit', description: 'The number of items per page | default is 10', required: false, example: '10' })
  @ApiResponse({ status: 200, description: 'Paginated list of chapters', type: [ChapterResponseDto] })
  async findAll(
    @Query('page') page?: number,
    @Query('limit') limit?: number,
  ): Promise<ApiResponseDto<ChapterResponseDto[]>> {
    page = page || 1;
    limit = limit || 10;
    const chapters = await this.chapterService.findAll(page, limit);
    return {
      success: true,
      message: 'Chapters retrieved successfully',
      data: chapters.data,
      page: chapters.page,
      limit: chapters.limit,
      totalRecords: chapters.total,
      totalPages: chapters.total ? Math.ceil(chapters.total / limit) : null,
      statusCode: HttpStatus.OK,
    };
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get a specific chapter by ID' })
  @ApiParam({ name: 'id', description: 'The ID of the chapter', example: '1' })
  @ApiResponse({ status: 200, description: 'The found chapter', type: ChapterResponseDto })
  @ApiResponse({ status: 404, description: 'Chapter not found' })
  async findOne(@Param('id') id: number, @Req() req: FastifyRequest & { user: User }): Promise<ApiResponseDto<ChapterResponseDto>> {
    const chapter = await this.chapterService.findOne(id);
    
    // exclude where chapter.newsfeeds[].is_public = false & chapter.user.id !== req.user.id
    chapter.newsfeeds = chapter?.newsfeeds?.filter((n) => n.is_public || chapter.user.id === req.user.id);

    return {
      success: true,
      message: 'Chapter retrieved successfully',
      data: chapter,
      statusCode: HttpStatus.OK,
    };
  }

  @Patch(':id')
  @ApiConsumes('application/json')
  @ApiProduces('application/json')
  @ApiOperation({ summary: 'Update a chapter' })
  @ApiParam({ name: 'id', description: 'The ID of the chapter to update', example: '1' })
  @ApiResponse({ status: 200, description: 'The chapter has been successfully updated', type: ChapterResponseDto })
  @ApiResponse({ status: 404, description: 'Chapter not found' })
  @ApiResponse({ status: 400, description: 'Bad Request' })
  @ApiBody({
    description: 'JSON payload to update a chapter',
    schema: {
      type: 'object',
      properties: {
        title: { type: 'string', example: 'Updated Chapter Title' },
        newsfeeds: {
          type: 'array',
          items: { type: 'number' },
          example: [2, 4],
        },
      },
      required: [],
    },
  })
  async update(
    @Param('id') id: number,
    @Body() body: UpdateChapterDto,
  ): Promise<ApiResponseDto<ChapterResponseDto>> {
    await this.chapterService.update(id, body);

    const reloaded = await this.chapterService.findOne(id);

    return {
      success: true,
      message: 'Chapter updated successfully',
      data: reloaded,
      statusCode: HttpStatus.OK,
    };
  }

  @Delete(':id')
  @ApiOperation({ summary: 'Delete a chapter' })
  @ApiParam({ name: 'id', description: 'The ID of the chapter to delete', example: '1' })
  @ApiResponse({ status: 200, description: 'The chapter has been successfully deleted' })
  @ApiResponse({ status: 404, description: 'Chapter not found' })
  async remove(@Param('id') id: number): Promise<ApiResponseDto<void>> {
    await this.chapterService.remove(id);
    return {
      success: true,
      message: 'Chapter deleted successfully',
      statusCode: HttpStatus.OK,
      data: undefined,
    };
  }
}
